import { ChangeEvent, useCallback, useContext, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table/Table";
import { InfoCircleFilled } from "@ant-design/icons";
import { IAssessmentTemplateListItem } from "@/modules/assessments/services/assessment-template-http.service";
import { useGetAssessmentTemplatesHook } from "@/modules/admin/scenes/hooks/useGetAssessmentTemplates.hook";

import TableHeader from "@/shared/components/TableHeader/TableHeader";
import { sortElements } from "@/shared/utils/array/sort.utils";
import { formatTableData } from "@/shared/utils/formaters/uiFormaters/tableFormaters";
import { longFormatDate, shortFormatDate } from "@/shared/utils/formaters/dataFormater/formatDate";
import buttonStyle from "@/ui-components/commonComponents/Button/style.module.css";
import { ASSESSMENT_TEMPLATES_ROUTE, NEW_ROUTE, NEW_VERSION_ROUTE } from "@/routing/AppRouter/routes.constants";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import cn from "classnames";
import tableStyle from "@/modules/tables/style.module.css";
import antTooltipOverlay from "@/shared/styles/antTooltipOverlay.module.css";
import style from "./style.module.css";
import debounce from "lodash.debounce";
import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination.constants";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";
import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { Loading } from "@/shared/components/Loading/Loading";
import { TemplateCreatedModal } from "./TemplateCreatedModal";

export const CREATED_URL_PARAM = {
  TEMPLATE_NAME: "createdName",
  CLIENT_NAME: "client",
  VERSION_NUMBER: "createdVersion",
};

const NEW_VERSION_HELPER_TEXT =
  "This should only be used to replace an existing assessment template. Once a new version is created, the previous template will no longer be available when creating new assessments.";

const columns: ColumnsType<IAssessmentTemplateListItem> = [
  {
    key: "name",
    title: "Name",
    dataIndex: "name",
    className: tableStyle.tableColumnName,
    defaultSortOrder: "ascend",
    sorter: (a, b) => sortElements(a.name, b.name),
  },
  {
    key: "version",
    title: "Version",
    dataIndex: "version",
    className: tableStyle.tableColumn,
    sorter: (a, b) => a.version - b.version,
  },
  {
    key: "createdAt",
    title: "Created On",
    dataIndex: "createdAt",
    className: tableStyle.tableColumn,
    render: (value: string) => formatTableData(value, shortFormatDate, longFormatDate, "—"),
    sorter: (a, b) => sortElements(a.createdAt, b.createdAt),
  },
  {
    key: "createdByName",
    title: "Created By",
    dataIndex: "createdByName",
    className: tableStyle.tableColumn,
    render: (value: string | null) => value ?? "—",
    sorter: (a, b) => sortElements(a.createdByName ?? "", b.createdByName ?? ""),
  },
  {
    key: "clientName",
    title: "Client",
    dataIndex: "clientName",
    className: tableStyle.tableColumn,
    render: (value: string | null) => value ?? "All Clients",
    sorter: (a, b) => sortElements(a.clientName ?? "All Clients", b.clientName ?? "All Clients"),
  },
  {
    key: "actions",
    title: (
      <p>
        Create New Version{" "}
        <Tooltip
          overlayClassName={antTooltipOverlay.tooltipWrapper}
          title={NEW_VERSION_HELPER_TEXT}
          trigger={["hover", "focus"]}
          placement="topRight"
          arrowPointAtCenter={true}
        >
          <span tabIndex={0} aria-label={`Tooltip for create new version: ${NEW_VERSION_HELPER_TEXT}`}>
            <InfoCircleFilled className={style.infoIcon} aria-hidden={true} />
          </span>
        </Tooltip>
      </p>
    ),
    className: tableStyle.tableColumn,
    render: (_: unknown, record: IAssessmentTemplateListItem) => (
      <Link
        className={cn(buttonStyle.linkAsButton, buttonStyle.linkAsButtonOutlined)}
        to={`${record.templateId}/${NEW_VERSION_ROUTE}`}
        aria-label={`Create new version for ${record.name}`}
      >
        Create New Version
      </Link>
    ),
  },
];

export default function AssessmentTemplatesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page") || 1);
  const pageSize = Number(searchParams.get("pageSize") || DEFAULT_PAGE_SIZE);
  const searchQuery = searchParams.get("search") ?? "";

  const options = useMemo(
    () => ({
      take: pageSize,
      skip: (currentPage - 1) * pageSize,
      search: searchQuery,
    }),
    [pageSize, currentPage, searchQuery],
  );

  const clientAbility = useContext(ClientAbilityContext);
  const canReadAssessmentTemplates = clientAbility.can(Action.Read, Subject.ASSESSMENT_TEMPLATE);
  useRouteAccessController({ slug: ASSESSMENT_TEMPLATES_ROUTE, hasPermission: canReadAssessmentTemplates });

  const { templates, totalCount, isLoading } = useGetAssessmentTemplatesHook(options);

  const createdTemplateName = searchParams.get(CREATED_URL_PARAM.TEMPLATE_NAME);
  const clientName = searchParams.get(CREATED_URL_PARAM.CLIENT_NAME);
  const parsedVersion = Number(searchParams.get(CREATED_URL_PARAM.VERSION_NUMBER));
  const createdVersion = Number.isFinite(parsedVersion) && parsedVersion > 0 ? parsedVersion : null;

  const clearCreatedTemplateParams = useCallback(() => {
    // Clear the created template params from the URL so the modal is not shown again on refresh/back.
    setSearchParams(
      (currentParams) => {
        if (
          !currentParams.has(CREATED_URL_PARAM.TEMPLATE_NAME) &&
          !currentParams.has(CREATED_URL_PARAM.CLIENT_NAME) &&
          !currentParams.has(CREATED_URL_PARAM.VERSION_NUMBER)
        )
          return currentParams;
        const newParams = new URLSearchParams(currentParams);
        newParams.delete(CREATED_URL_PARAM.TEMPLATE_NAME);
        newParams.delete(CREATED_URL_PARAM.CLIENT_NAME);
        newParams.delete(CREATED_URL_PARAM.VERSION_NUMBER);
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const applySearchToUrl = useMemo(
    () =>
      debounce((value: string) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value.trim()) {
            next.set("search", value);
          } else {
            next.delete("search");
          }
          next.set("page", "1");
          return next;
        });
      }, 400),
    [setSearchParams],
  );

  useEffect(() => {
    return () => {
      applySearchToUrl.cancel();
    };
  }, [applySearchToUrl]);

  if (isLoading) {
    return <Loading />;
  }

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    if (currentPage !== 1) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      });
    }
    applySearchToUrl(value);
  }

  function setTableCurrentPage(page: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(page));
    setSearchParams(next);
  }

  return (
    <div className="main-container">
      <SEO titleParams={["Assessment Templates"]} />
      <div className="inner-container">
        <div className={tableStyle.tableBlock}>
          <TableHeader
            tableName="Assessment Templates"
            handleSearchChange={handleSearchChange}
            initialSearchQuery={searchQuery}
          >
            <Link to={NEW_ROUTE} className={cn(buttonStyle.linkAsButton, buttonStyle.linkAsButtonPrimary)}>
              New Assessment Template
            </Link>
          </TableHeader>
          <Table
            columns={columns}
            dataSource={templates}
            rowKey="templateId"
            loading={isLoading}
            pagination={{
              position: ["bottomLeft"],
              total: totalCount,
              current: currentPage,
              onChange: setTableCurrentPage,
              defaultPageSize: DEFAULT_PAGE_SIZE,
            }}
          />
        </div>
      </div>

      <TemplateCreatedModal
        templateName={createdTemplateName}
        clientName={clientName}
        versionNumber={createdVersion}
        onClose={clearCreatedTemplateParams}
      />
    </div>
  );
}
